import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Env } from '@/types';
import {
  AuthError,
  clearTokens,
  isExpired,
  loadTokens,
  parseTokenClaims,
  saveTokens,
  signInWithPassword,
  type PasswordAuthTokens,
} from '@/api/passwordAuth';
import {
  DiscoveryError,
  listOrganizations,
  listWorkspaces,
  type Organization,
  type Workspace,
} from '@/api/discovery';

interface AuthState {
  // Environment
  env: Env;
  setEnv: (env: Env) => void;

  // Auth
  tokens: PasswordAuthTokens | null;
  email?: string;
  name?: string;
  loading: boolean;
  error: AuthError | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithToken: (accessToken: string, opts?: { idToken?: string; refreshToken?: string }) => void;
  signOut: () => void;
  /** Returns the access token if it's still valid, otherwise null. The
   *  marketing-site auth proxy doesn't issue refresh tokens so we don't
   *  attempt automatic refresh — the user is asked to sign in again. */
  getValidAccessToken: () => string | null;

  // Org / workspace selection (auto-discovered after sign-in)
  organizations: Organization[];
  workspaces: Workspace[];
  organizationId: string;
  workspaceId: string;
  setOrganizationId: (id: string) => void;
  setWorkspaceId: (id: string) => void;
  /** True while orgs/workspaces are being fetched in the background. */
  discoveringContext: boolean;
  contextError: string | null;
  reloadContext: () => void;
}

const ORG_ID_KEY = 'libertify_pg_org_id';
const WORKSPACE_ID_KEY = 'libertify_pg_workspace_id';

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({
  initialEnv,
  children,
}: {
  initialEnv: Env;
  children: ReactNode;
}) {
  const [env, setEnv] = useState<Env>(initialEnv);

  const [tokens, setTokens] = useState<PasswordAuthTokens | null>(() => loadTokens());
  const [identity, setIdentity] = useState<{ email?: string; name?: string }>(() => {
    const t = loadTokens();
    return t ? parseTokenClaims(t.idToken || t.accessToken) : {};
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [organizationId, setOrganizationIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(ORG_ID_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [workspaceId, setWorkspaceIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(WORKSPACE_ID_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [discoveringContext, setDiscoveringContext] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  // Persist selected org/workspace.
  const setOrganizationId = useCallback((id: string) => {
    setOrganizationIdState(id);
    try {
      if (id) localStorage.setItem(ORG_ID_KEY, id);
      else localStorage.removeItem(ORG_ID_KEY);
    } catch {
      /* ignore */
    }
    // Switching org clears the chosen workspace — it belongs to the old org.
    setWorkspaceIdState('');
    try {
      localStorage.removeItem(WORKSPACE_ID_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const setWorkspaceId = useCallback((id: string) => {
    setWorkspaceIdState(id);
    try {
      if (id) localStorage.setItem(WORKSPACE_ID_KEY, id);
      else localStorage.removeItem(WORKSPACE_ID_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // -------------------------------------------------------------------
  // Token lifecycle
  // -------------------------------------------------------------------

  const signIn = async (email: string, password: string): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const fresh = await signInWithPassword(env, email, password);
      saveTokens(fresh);
      setTokens(fresh);
      setIdentity(parseTokenClaims(fresh.idToken || fresh.accessToken));
    } catch (e) {
      const err =
        e instanceof AuthError
          ? e
          : new AuthError('UnknownError', e instanceof Error ? e.message : String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithToken = (
    accessToken: string,
    opts?: { idToken?: string; refreshToken?: string }
  ): void => {
    setError(null);
    const next: PasswordAuthTokens = {
      accessToken,
      idToken: opts?.idToken,
      refreshToken: opts?.refreshToken,
      obtainedAt: Date.now(),
    };
    saveTokens(next);
    setTokens(next);
    setIdentity(parseTokenClaims(next.idToken || next.accessToken));
  };

  const signOut = useCallback(() => {
    clearTokens();
    setTokens(null);
    setIdentity({});
    setError(null);
    setOrganizations([]);
    setWorkspaces([]);
    setContextError(null);
  }, []);

  const getValidAccessToken = useCallback((): string | null => {
    if (!tokens) return null;
    if (isExpired(tokens)) {
      // No refresh token available — user must sign in again.
      clearTokens();
      setTokens(null);
      setIdentity({});
      return null;
    }
    return tokens.accessToken;
  }, [tokens]);

  // Switching env invalidates the existing token (different user pool).
  useEffect(() => {
    if (tokens) {
      clearTokens();
      setTokens(null);
      setIdentity({});
      setOrganizations([]);
      setWorkspaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env]);

  // -------------------------------------------------------------------
  // Auto-discovery: orgs after sign-in, workspaces after org selection
  // -------------------------------------------------------------------

  // Track in-flight discovery to ignore stale responses.
  const discoveryToken = useRef(0);

  const reloadOrgs = useCallback(async () => {
    if (!tokens) return;
    const tag = ++discoveryToken.current;
    setDiscoveringContext(true);
    setContextError(null);
    try {
      const orgs = await listOrganizations(env, tokens.accessToken);
      if (tag !== discoveryToken.current) return;
      setOrganizations(orgs);
      // Auto-select the first org if none persisted, or if the persisted
      // one isn't in the list (different env/user/etc).
      setOrganizationIdState((prev) => {
        if (prev && orgs.some((o) => o.id === prev)) return prev;
        return orgs[0]?.id ?? '';
      });
    } catch (e) {
      if (tag !== discoveryToken.current) return;
      // 401 = token rejected → force re-sign-in.
      if (e instanceof DiscoveryError && e.status === 401) {
        clearTokens();
        setTokens(null);
        setIdentity({});
        setContextError(null);
        return;
      }
      setContextError(e instanceof Error ? e.message : String(e));
    } finally {
      if (tag === discoveryToken.current) setDiscoveringContext(false);
    }
  }, [env, tokens]);

  const reloadWorkspaces = useCallback(async () => {
    if (!tokens || !organizationId) {
      setWorkspaces([]);
      return;
    }
    const tag = ++discoveryToken.current;
    try {
      const ws = await listWorkspaces(env, tokens.accessToken, organizationId);
      if (tag !== discoveryToken.current) return;
      setWorkspaces(ws);
      setWorkspaceIdState((prev) => {
        if (prev && ws.some((w) => w.id === prev)) return prev;
        return ws[0]?.id ?? '';
      });
    } catch (e) {
      if (tag !== discoveryToken.current) return;
      // A workspace listing failure isn't fatal — projects can be created
      // without an explicit workspace_id.
      setWorkspaces([]);
      // eslint-disable-next-line no-console
      console.warn('[playground] failed to list workspaces:', e);
    }
  }, [env, tokens, organizationId]);

  const reloadContext = useCallback(() => {
    void reloadOrgs();
  }, [reloadOrgs]);

  // Fetch orgs whenever a (new) token appears.
  useEffect(() => {
    if (tokens) void reloadOrgs();
    else {
      setOrganizations([]);
      setWorkspaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  // Fetch workspaces whenever the active org changes.
  useEffect(() => {
    if (tokens && organizationId) void reloadWorkspaces();
    else setWorkspaces([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, tokens]);

  // -------------------------------------------------------------------
  // Memoized context value
  // -------------------------------------------------------------------

  const value = useMemo<AuthState>(
    () => ({
      env,
      setEnv,
      tokens,
      email: identity.email,
      name: identity.name,
      loading,
      error,
      signIn,
      signInWithToken,
      signOut,
      getValidAccessToken,
      organizations,
      workspaces,
      organizationId,
      workspaceId,
      setOrganizationId,
      setWorkspaceId,
      discoveringContext,
      contextError,
      reloadContext,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      env,
      tokens,
      identity,
      loading,
      error,
      organizations,
      workspaces,
      organizationId,
      workspaceId,
      discoveringContext,
      contextError,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
